/**
 * UI-8e Task 5 — NewFlagDrawer.
 *
 * Slide-in drawer for creating a brand-new feature flag. Composed of:
 *
 *   - flagKey         InputText (lowercase snake_case, regex-validated)
 *   - enabled         SelectButton OFF/ON
 *   - rolloutPct      Slider 0..100 with live readout
 *
 * Submit calls `useFeatureFlags().createGlobal({ flagKey, enabled, rolloutPct })`
 * which POSTs `/platform/feature-flags` and refetches the grouped list.
 *
 * Validation:
 *   - Server-side regex (Task 2): `/^[a-z][a-z0-9_]{2,63}$/`. Mirrored here
 *     so Save stays disabled until the input parses; on the first invalid
 *     keystroke the inline error appears.
 *
 * Why not <ZodForm>? Phase 9a's form framework is the standard for typed
 * forms, but this drawer is intentionally tiny (3 fields, no nested
 * resolvers) — direct useState matches the sibling NewKey/Schedule
 * drawers and keeps the diff scoped. Future refactor welcome.
 */
import { useState, type JSX } from 'react'
import { Slider } from 'primereact/slider'
import { Flag, X } from 'lucide-react'

import { useFeatureFlags } from '../../hooks/useFeatureFlags.js'

export interface NewFlagDrawerProps {
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

const FLAG_KEY_REGEX = /^[a-z][a-z0-9_]{2,63}$/

export function NewFlagDrawer({ open, onClose, onSaved }: NewFlagDrawerProps): JSX.Element {
  const { createGlobal } = useFeatureFlags()
  const [flagKey, setFlagKey] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [rolloutPct, setRolloutPct] = useState(0)
  const [saving, setSaving] = useState(false)

  const valid = FLAG_KEY_REGEX.test(flagKey)
  const showRegexError = flagKey.length > 0 && !valid

  async function handleSave(): Promise<void> {
    if (!valid) return
    setSaving(true)
    try {
      await createGlobal({ flagKey, enabled, rolloutPct })
      setFlagKey('')
      setEnabled(false)
      setRolloutPct(0)
      onSaved?.()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  if (!open) return <></>

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[480px] bg-white shadow-2xl z-[9999] flex flex-col transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <Flag className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">New Feature Flag</h2>
              <p className="text-xs text-gray-400">Create a new global flag with rollout settings.</p>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Flag Configuration</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-flag-key" className="text-sm font-semibold text-gray-700">Flag Key <span className="text-red-500">*</span></label>
                <input
                  id="new-flag-key"
                  type="text"
                  value={flagKey}
                  onChange={(e) => setFlagKey(e.target.value)}
                  placeholder="enable_my_feature"
                  className={`w-full rounded-xl border px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all ${
                    showRegexError ? 'border-red-300 bg-red-50/30 focus:border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 bg-gray-50/50 focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10'
                  }`}
                />
                {showRegexError && <p className="text-xs text-red-500">Must match: {FLAG_KEY_REGEX.source}</p>}
                {!showRegexError && <p className="text-xs text-gray-400">Lowercase letters, numbers, underscores. 3-64 chars.</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Enabled</label>
                <div className="flex gap-1">
                  {[
                    { label: 'OFF', value: false },
                    { label: 'ON', value: true },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setEnabled(opt.value)}
                      className={`flex-1 px-4 py-2 text-xs font-semibold rounded-sm transition-all border ${
                        enabled === opt.value
                          ? opt.value ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">Rollout <span className="text-gray-400 font-normal ml-1">{rolloutPct}%</span></label>
                <Slider
                  value={rolloutPct}
                  onChange={(e) => { if (typeof e.value === 'number') setRolloutPct(e.value) }}
                  min={0} max={100}
                  className="mx-1"
                />
                <div className="flex justify-between text-[10px] text-gray-300 px-1">
                  <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-white shrink-0">
          <button onClick={onClose} className="text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={!valid || saving}
            className="text-sm font-semibold px-5 py-2.5 rounded-sm bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 text-white transition-all flex items-center gap-2"
          >
            {saving && <i className="pi pi-spin pi-spinner text-xs" />}
            Create Flag
          </button>
        </div>
      </div>
    </>
  )
}

export default NewFlagDrawer
